/*
 * The check-in desk's state, shared with the classic panel.
 *
 * The one rule that must survive any redesign is TICK-062 R8: **a QR admits a whole order, once.**
 * There is no partial admission, and a group order requires an explicit acknowledgement that
 * everybody is present before it can be confirmed. Admin is a convenience surface for the same
 * physical act as the check-in app, so if it were the lax way to confirm, the contract would only
 * hold for whoever happened to use the other screen.
 *
 * `throughput` is the one figure this file derives. The Command Center says elsewhere that a scan
 * rate needs a window nobody has chosen — so this chooses one, names it on screen, and reports
 * honestly when the source cannot cover it: the check-in log returns the most recent 200 rows, so
 * a window busy enough to fill it is reported as a floor rather than a rate.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { adminApi, ApiError, formatCustomFieldValues, wristbandReceivedLabel } from '../../../api';
import type { AdminOrderDto, AdminStatsDto, CheckinLogDto, CheckinLogEntryDto } from '../../../api';
import {
  bundleAgeHours, bundleKey, exceptionOf, exceptionsKey, indexBundle, lookupOffline, queueKey,
  readStored, remainingAfter, writeStored,
} from './offlineCheckin';
import type {
  OfflineCheckinBundleDto, OfflineCheckinOrderDto, OfflineIndex, QueuedAdmission, ReplayException,
  ReplayVerdict,
} from './offlineCheckin';

/** The window the rate is measured over. Named on screen, never implied. */
export const RATE_WINDOW_MINUTES = 15;
/** What `getCheckinLog` returns at most. A full window means the real count is higher. */
export const LOG_LIMIT = 200;

export interface Throughput {
  /** Admissions per minute over the window, or null when nothing was scanned in it. */
  perMinute: number | null;
  counted: number;
  /** The log ran out before the window did, so `counted` is a floor and the rate is understated. */
  saturated: boolean;
}

export function throughput(
  entries: readonly CheckinLogEntryDto[],
  now = Date.now(),
  windowMinutes = RATE_WINDOW_MINUTES,
): Throughput {
  const since = now - windowMinutes * 60_000;
  const inWindow = entries.filter((e) => {
    const at = new Date(e.createdAt).getTime();
    return Number.isFinite(at) && at >= since && at <= now;
  });
  return {
    perMinute: inWindow.length === 0 ? null : Number((inWindow.length / windowMinutes).toFixed(1)),
    counted: inWindow.length,
    // Every row the endpoint returned fell inside the window, so there were probably more it cut.
    saturated: entries.length >= LOG_LIMIT && inWindow.length === entries.length,
  };
}

export type ScanOutcome =
  | { kind: 'admitted'; code: string; name: string; lines: string; entries: number }
  /** Recorded on this device while offline. A fact in the world, not yet a fact on the server. */
  | { kind: 'provisional'; code: string; name: string; entries: number; queued: number }
  | { kind: 'already'; code: string; detail: string }
  | { kind: 'unverified'; code: string; name: string }
  | { kind: 'error'; detail: string };

/**
 * What the confirmation panel reads, from either source.
 *
 * Online it is built from the order the server returned; offline from the snapshot. One shape means
 * the R8 acknowledgement, the wristband list and the entry count are rendered by one piece of code
 * — a second copy for the offline path is how the two would come to disagree about what admitting
 * a group requires.
 */
export interface PendingAdmission {
  code: string;
  name: string;
  contact: string;
  entries: number;
  qty: number;
  lines: { key: string; label: string; custom: string }[];
  wristbands: { key: string; name: string; color: string; qty: number }[];
  source: 'server' | 'offline';
}

function fromOrder(order: AdminOrderDto): PendingAdmission {
  return {
    code: order.code,
    name: order.name || '—',
    contact: [order.phone, order.email].filter(Boolean).join(' · ') || '—',
    entries: order.entries,
    qty: order.qty,
    lines: order.lines.map((l) => ({
      key: l.ticketId,
      label: l.label,
      custom: formatCustomFieldValues(l.customFieldValues ?? []),
    })),
    wristbands: order.wristbands.map((w) => ({ key: w.id, name: w.name, color: w.color, qty: w.qty })),
    source: 'server',
  };
}

function fromSnapshot(order: OfflineCheckinOrderDto): PendingAdmission {
  return {
    code: order.code,
    name: order.name || '—',
    // The snapshot deliberately carries no phone or email: a gate does not need them to admit
    // somebody, and a bundle sitting on a borrowed device should not be a contact list.
    contact: '—',
    entries: order.entries,
    qty: order.qty,
    lines: order.lines.map((l, i) => ({ key: `${order.code}-${i}`, label: l.label, custom: '' })),
    wristbands: order.wristbands.map((w, i) => ({ key: `${order.code}-${i}`, ...w })),
    source: 'offline',
  };
}

export type BundleStatus = 'none' | 'loading' | 'ready' | 'failed';

export interface CheckinState {
  /** The order looked up and awaiting a decision. Null while the desk is idle. */
  pending: PendingAdmission | null;
  outcome: ScanOutcome | null;
  log: CheckinLogDto | null;
  stats: AdminStatsDto | null;
  rate: Throughput;
  busy: boolean;
  confirming: boolean;
  /** R8 — a group order cannot be confirmed until this is ticked. */
  acknowledged: boolean;
  setAcknowledged: (next: boolean) => void;
  lookup: (payload: string) => Promise<void>;
  confirm: () => Promise<void>;
  cancel: () => void;
  clearOutcome: () => void;
  reloadLog: () => Promise<void>;

  /* ── degraded offline ─────────────────────────────────────────────────────────────────────── */

  /** What the desk believes about the network. A failed request is believed over `navigator`. */
  online: boolean;
  bundle: OfflineIndex | null;
  bundleStatus: BundleStatus;
  /** How old the snapshot is, in hours. The desk says so rather than implying it is current. */
  bundleAge: number | null;
  /** Admissions made offline and not yet accepted by the server. */
  queue: QueuedAdmission[];
  /** False when the queue could not be written to disk — a reload would lose it, and it says so. */
  queuePersisted: boolean;
  /** Replays the server refused. Each needs a person; they do not clear themselves. */
  exceptions: ReplayException[];
  syncing: boolean;
  refreshBundle: () => Promise<void>;
  syncNow: () => Promise<void>;
  dismissException: (id: string) => void;
}

export function useCheckin(token: string, eventSlug: string | undefined): CheckinState {
  const [pending, setPending] = useState<PendingAdmission | null>(null);
  const [outcome, setOutcome] = useState<ScanOutcome | null>(null);
  const [log, setLog] = useState<CheckinLogDto | null>(null);
  const [stats, setStats] = useState<AdminStatsDto | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);

  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false));
  const [bundle, setBundle] = useState<OfflineIndex | null>(null);
  const [bundleStatus, setBundleStatus] = useState<BundleStatus>('none');
  const [queue, setQueue] = useState<QueuedAdmission[]>(() => readStored<QueuedAdmission[]>(queueKey(eventSlug)) ?? []);
  const [queuePersisted, setQueuePersisted] = useState(true);
  const [exceptions, setExceptions] = useState<ReplayException[]>(
    () => readStored<ReplayException[]>(exceptionsKey(eventSlug)) ?? [],
  );
  const [syncing, setSyncing] = useState(false);

  /** Guards the scanner, which fires repeatedly while a code is in frame. */
  const inFlight = useRef(false);
  const payloadRef = useRef('');
  /** The queue as the replay loop sees it, so a scan during a sync cannot be replayed twice. */
  const queueRef = useRef(queue);
  queueRef.current = queue;

  /*
   * `navigator.onLine` is a hint, not an answer: it is true on a wifi with no route out, which is
   * the exact failure a venue produces. A request that fails at the network layer is believed over
   * it, and a request that succeeds puts the desk back online.
   */
  const noteReachable = useCallback((reachable: boolean) => setOnline(reachable), []);

  const persistQueue = useCallback((next: QueuedAdmission[]) => {
    setQueue(next);
    queueRef.current = next;
    setQueuePersisted(writeStored(queueKey(eventSlug), next));
  }, [eventSlug]);

  const persistExceptions = useCallback((next: ReplayException[]) => {
    setExceptions(next);
    writeStored(exceptionsKey(eventSlug), next);
  }, [eventSlug]);

  const reloadLog = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        adminApi.getCheckinLog(token, eventSlug),
        adminApi.getStats(token, eventSlug).catch(() => null),
      ]);
      setLog(l);
      if (s) setStats(s);
      noteReachable(true);
    } catch (e) {
      // The desk works without the record beside it; a failed read must not block a scan.
      if (!(e instanceof ApiError)) noteReachable(false);
    }
  }, [token, eventSlug, noteReachable]);

  const refreshBundle = useCallback(async () => {
    setBundleStatus('loading');
    try {
      const fresh = await adminApi.getOfflineCheckinBundle(token, eventSlug);
      setBundle(indexBundle(fresh));
      setBundleStatus('ready');
      writeStored(bundleKey(eventSlug), fresh);
      noteReachable(true);
    } catch (e) {
      /*
       * A snapshot from the last time the network worked is worth more than none, so a failed
       * refresh falls back to whatever is on disk rather than clearing what the gate is holding.
       */
      const stored = readStored<OfflineCheckinBundleDto>(bundleKey(eventSlug));
      if (stored) {
        setBundle(indexBundle(stored));
        setBundleStatus('ready');
      } else {
        setBundleStatus('failed');
      }
      if (!(e instanceof ApiError)) noteReachable(false);
    }
  }, [token, eventSlug, noteReachable]);

  useEffect(() => { void reloadLog(); void refreshBundle(); }, [reloadLog, refreshBundle]);

  /**
   * Replay every queued admission, oldest first, and settle each one.
   *
   * Order matters: two admissions of the same order can only be told apart by which reached the
   * server first, and replaying oldest-first makes that the one that actually happened first.
   */
  const syncNow = useCallback(async () => {
    if (syncing || queueRef.current.length === 0) return;
    setSyncing(true);
    const verdicts: ReplayVerdict[] = [];
    try {
      for (const item of [...queueRef.current].sort((a, b) => a.admittedAt.localeCompare(b.admittedAt))) {
        try {
          const r = await adminApi.checkinConfirm(token, item.payload, item.entries, eventSlug);
          verdicts.push(r.alreadyCheckedIn
            ? { kind: 'conflict', item, detail: wristbandReceivedLabel(r.checkedInAt!) }
            : { kind: 'accepted', item });
          noteReachable(true);
        } catch (e) {
          if (e instanceof ApiError) {
            // The server was reached and refused: a decision, not a retry.
            verdicts.push({ kind: 'rejected', item, detail: e.message });
            noteReachable(true);
          } else {
            // Still no network. Stop the pass rather than hammering it order by order.
            verdicts.push({ kind: 'deferred', item });
            noteReachable(false);
            break;
          }
        }
      }
      persistQueue(remainingAfter(queueRef.current, verdicts));
      const raised = verdicts.map(exceptionOf).filter((x): x is ReplayException => x !== null);
      if (raised.length > 0) persistExceptions([...raised, ...exceptions]);
      await reloadLog();
    } finally {
      setSyncing(false);
    }
  }, [syncing, token, eventSlug, persistQueue, persistExceptions, exceptions, reloadLog, noteReachable]);

  /* The browser's own reconnect signal is the cheapest trigger; a scan that succeeds is the other. */
  useEffect(() => {
    const onOnline = () => { setOnline(true); void syncNow(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [syncNow]);

  const lookup = useCallback(async (raw: string) => {
    const payload = raw.trim();
    if (!payload || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setOutcome(null);
    try {
      const order = await adminApi.checkinLookup(token, payload, eventSlug);
      noteReachable(true);
      if (order.unverified) {
        setOutcome({ kind: 'unverified', code: order.code, name: order.name || '—' });
      } else if (order.alreadyCheckedIn) {
        setOutcome({ kind: 'already', code: order.code, detail: wristbandReceivedLabel(order.checkedInAt!) });
      } else {
        payloadRef.current = payload;
        setPending(fromOrder(order));
        setAcknowledged(false);
      }
    } catch (e) {
      if (e instanceof ApiError) {
        // The server answered. Its refusal is the answer, online or not.
        setOutcome({ kind: 'error', detail: e.message });
      } else {
        noteReachable(false);
        await lookupFromSnapshot(payload);
      }
    } finally {
      setBusy(false);
      // The scanner re-fires while the code stays in frame; a short gate stops a double lookup.
      setTimeout(() => { inFlight.current = false; }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `lookupFromSnapshot` is defined below
  }, [token, eventSlug, noteReachable, bundle, queue]);

  async function lookupFromSnapshot(payload: string) {
    if (!bundle) {
      setOutcome({
        kind: 'error',
        detail: 'ออฟไลน์อยู่ และเครื่องนี้ยังไม่มีสำเนารายชื่อไว้ใช้ — ต่อเน็ตแล้วเปิดหน้านี้อีกครั้งก่อนเริ่มงาน',
      });
      return;
    }
    const found = await lookupOffline(bundle, queueRef.current, payload);
    if (found.kind === 'unknown') {
      setOutcome({ kind: 'error', detail: 'ออฟไลน์อยู่ — ไม่พบรหัสนี้ในสำเนารายชื่อของเครื่องนี้' });
      return;
    }
    if (found.kind === 'already') {
      setOutcome({ kind: 'already', code: found.order.code, detail: wristbandReceivedLabel(found.at) });
      return;
    }
    if (found.kind === 'queued') {
      setOutcome({
        kind: 'already',
        code: found.order.code,
        detail: `เครื่องนี้เช็กอินไปแล้วตอน ${new Date(found.queued.admittedAt).toLocaleTimeString('th-TH')} และยังรอส่งขึ้นระบบ`,
      });
      return;
    }
    payloadRef.current = payload;
    setPending(fromSnapshot(found.order));
    setAcknowledged(false);
  }

  const confirm = useCallback(async () => {
    if (!pending || confirming) return;
    setConfirming(true);
    try {
      if (!online || pending.source === 'offline') {
        /*
         * Queued rather than sent. The person is admitted in the physical world the moment staff
         * hand over the wristband, so the record is written here and reconciled later — the reverse
         * order would mean a network outage could unadmit somebody already inside.
         */
        const item: QueuedAdmission = {
          id: crypto.randomUUID(),
          code: pending.code,
          payload: payloadRef.current,
          name: pending.name,
          entries: pending.entries,
          admittedAt: new Date().toISOString(),
        };
        const next = [...queueRef.current, item];
        persistQueue(next);
        setOutcome({
          kind: 'provisional', code: item.code, name: item.name, entries: item.entries, queued: next.length,
        });
        setPending(null);
        setAcknowledged(false);
        return;
      }

      const r = await adminApi.checkinConfirm(token, payloadRef.current, pending.entries, eventSlug);
      noteReachable(true);
      if (r.alreadyCheckedIn) {
        setOutcome({ kind: 'already', code: r.code, detail: wristbandReceivedLabel(r.checkedInAt!) });
      } else {
        setOutcome({
          kind: 'admitted',
          code: r.code,
          name: r.name || '—',
          lines: r.lines.map((l) => l.label).join(' · '),
          entries: r.entries,
        });
      }
      setPending(null);
      setAcknowledged(false);
      void reloadLog();
    } catch (e) {
      if (e instanceof ApiError) {
        setOutcome({ kind: 'error', detail: e.message });
      } else {
        // The network died between lookup and confirm. Queue it rather than lose it.
        noteReachable(false);
        const item: QueuedAdmission = {
          id: crypto.randomUUID(),
          code: pending.code,
          payload: payloadRef.current,
          name: pending.name,
          entries: pending.entries,
          admittedAt: new Date().toISOString(),
        };
        const next = [...queueRef.current, item];
        persistQueue(next);
        setOutcome({
          kind: 'provisional', code: item.code, name: item.name, entries: item.entries, queued: next.length,
        });
      }
      setPending(null);
      setAcknowledged(false);
    } finally {
      setConfirming(false);
    }
  }, [pending, confirming, online, token, eventSlug, persistQueue, reloadLog, noteReachable]);

  const cancel = useCallback(() => { setPending(null); setAcknowledged(false); }, []);

  return {
    pending, outcome, log, stats,
    rate: throughput(log?.entries ?? []),
    busy, confirming, acknowledged, setAcknowledged,
    lookup, confirm, cancel,
    clearOutcome: () => setOutcome(null),
    reloadLog,
    online, bundle, bundleStatus,
    bundleAge: bundle ? bundleAgeHours(bundle.generatedAt) : null,
    queue, queuePersisted, exceptions, syncing,
    refreshBundle, syncNow,
    dismissException: (id: string) => persistExceptions(exceptions.filter((x) => x.id !== id)),
  };
}
